import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import toast from 'react-hot-toast';
import api from '../services/api';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingToken, setPendingToken] = useState(null);
  const [pendingEmail, setPendingEmail] = useState('');
  const navigate = useNavigate();

  // Check if user is logged in on initial load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwt_decode(token);
        // Check if token is expired
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
          // Token is expired
          logout();
        } else {
          // Fetch current user data from database to ensure we have the latest information
          fetchCurrentUser(decoded.id);
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        logout();
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch current user data from database
  const fetchCurrentUser = async (userId) => {
    try {
      const response = await api.get(`/api/users/${userId}`);
      setCurrentUser(response.data.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
      // If we can't fetch user data, fall back to token data or logout
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const decoded = jwt_decode(token);
          setCurrentUser({
            id: decoded.id,
            email: decoded.email,
            name: decoded.name,
            role: decoded.role,
            department: decoded.department
          });
        } catch (decodeError) {
          console.error('Error decoding token as fallback:', decodeError);
          logout();
        }
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError('');

      const response = await api.post('/api/auth/login', { email, password });
      const { token, user, requiresOtp, pendingToken: pt } = response.data;

      if (requiresOtp) {
        setPendingToken(pt);
        setPendingEmail(email);
        navigate('/verify-otp');
        return false;
      }

      localStorage.setItem('token', token);
      localStorage.setItem('userRole', user.role);
      setCurrentUser(user);
      // La réponse de connexion est minimale : on recharge la fiche complète
      // (intérim en cours, signataire) sans attendre un rafraîchissement de page.
      fetchCurrentUser(user.id);
      toast.success('Connexion réussie');
      navigate('/dashboard');
      return true;
    } catch (error) {
      const msg = error.response?.data?.error || 'Erreur de connexion';
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (otp, rememberDevice) => {
    try {
      if (!pendingToken) {
        navigate('/login');
        return false;
      }
      setLoading(true);
      setError('');

      const response = await api.post(
        '/api/auth/verify-otp',
        { otp, rememberDevice },
        { headers: { Authorization: `Bearer ${pendingToken}` } }
      );
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('userRole', user.role);
      setCurrentUser(user);
      fetchCurrentUser(user.id);
      setPendingToken(null);
      setPendingEmail('');
      toast.success('Connexion réussie');
      navigate('/dashboard');
      return true;
    } catch (error) {
      const data = error.response?.data;
      const base = data?.error || 'Code invalide';
      const msg =
        data?.attemptsLeft !== undefined
          ? `${base} — ${data.attemptsLeft} tentative${data.attemptsLeft > 1 ? 's' : ''} restante${data.attemptsLeft > 1 ? 's' : ''}`
          : base;
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    try {
      if (!pendingToken) {
        navigate('/login');
        return false;
      }
      setLoading(true);
      setError('');

      const response = await api.post(
        '/api/auth/resend-otp',
        {},
        { headers: { Authorization: `Bearer ${pendingToken}` } }
      );
      setPendingToken(response.data.pendingToken);
      toast.success('Nouveau code envoyé');
      return true;
    } catch (error) {
      const msg = error.response?.data?.error || 'Erreur lors du renvoi';
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (e) {
      // ignore — clear local state regardless
    }
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    setCurrentUser(null);
    navigate('/login');
  };

  // Register function (admin only)
  const register = async (userData) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.post('/api/auth/register', userData);
      toast.success('Utilisateur créé avec succès');
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      setError(error.response?.data?.message || 'Erreur lors de la création de l\'utilisateur');
      toast.error(error.response?.data?.message || 'Erreur lors de la création de l\'utilisateur');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      setError('');
      
      await api.post('/api/auth/forgot-password', { email });
      toast.success('Instructions envoyées à votre email');
      return true;
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.response?.data?.message || 'Erreur lors de la demande de réinitialisation');
      toast.error(error.response?.data?.message || 'Erreur lors de la demande de réinitialisation');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (token, password) => {
    try {
      setLoading(true);
      setError('');
      
      await api.post('/api/auth/reset-password', { token, password });
      toast.success('Mot de passe réinitialisé avec succès');
      navigate('/login');
      return true;
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe');
      toast.error(error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      setLoading(true);
      setError('');
      
      if (!currentUser || (!currentUser.id && !currentUser._id)) {
        throw new Error('Utilisateur non connecté ou ID manquant');
      }
      
      const userId = currentUser.id || currentUser._id;
      const response = await api.put(`/api/users/${userId}`, userData);
      setCurrentUser({ ...currentUser, ...response.data.data });
      toast.success('Profil mis à jour avec succès');
      return response.data;
    } catch (error) {
      console.error('Update profile error:', error);
      setError(error.response?.data?.message || 'Erreur lors de la mise à jour du profil');
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour du profil');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Upload signature
  const uploadSignature = async (signatureData) => {
    try {
      setLoading(true);
      setError('');
      
      if (!currentUser || (!currentUser.id && !currentUser._id)) {
        throw new Error('Utilisateur non connecté ou ID manquant');
      }
      
      const userId = currentUser.id || currentUser._id;
      const formData = new FormData();
      formData.append('signature', signatureData);
      
      const response = await api.put(`/api/users/${userId}/signature`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setCurrentUser({ ...currentUser, signatureUrl: response.data.data.signatureUrl });
      toast.success('Signature mise à jour avec succès');
      return response.data;
    } catch (error) {
      console.error('Upload signature error:', error);
      setError(error.response?.data?.message || 'Erreur lors de la mise à jour de la signature');
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour de la signature');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Upload stamp
  const uploadStamp = async (stampData) => {
    try {
      setLoading(true);
      setError('');
      
      if (!currentUser || (!currentUser.id && !currentUser._id)) {
        throw new Error('Utilisateur non connecté ou ID manquant');
      }
      
      const userId = currentUser.id || currentUser._id;
      const formData = new FormData();
      formData.append('stamp', stampData);
      
      const response = await api.put(`/api/users/${userId}/stamp`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setCurrentUser({ ...currentUser, stampUrl: response.data.data.stampUrl });
      toast.success('Cachet mis à jour avec succès');
      return response.data;
    } catch (error) {
      console.error('Upload stamp error:', error);
      setError(error.response?.data?.message || 'Erreur lors de la mise à jour du cachet');
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour du cachet');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    pendingEmail,
    login,
    logout,
    register,
    forgotPassword,
    resetPassword,
    updateProfile,
    uploadSignature,
    uploadStamp,
    verifyOtp,
    resendOtp,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
